import { ChangeDetectionStrategy, Component, effect, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ComplexArticleBlueprintListComponent } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint-list/complex-article-blueprint-list.component';
import { ComplexArticleBlueprintDTO, ComplexArticleBlueprintVersionOverwriteMode } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint';
import { ComplexArticleBlueprintService, ComplexArticleBlueprintValidation } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Log } from '@shared/debug/log';
import { ModalComponent } from '@shared/modal/modal.component';
import { RouteNavigationService } from '@services/route-navigation.service';
import { BsModalRef } from 'ngx-bootstrap/modal';

@Component({
  selector: 'app-complex-article-blueprints',
  imports: [
    ModalComponent,
    TranslateModule,
    FormsModule,
    ComplexArticleBlueprintListComponent,
  ],
  templateUrl: './complex-article-blueprints.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComplexArticleBlueprintsComponent {
  private readonly bsModalRef = inject(BsModalRef);
  private readonly complexArticleBlueprintService = inject(ComplexArticleBlueprintService);
  private readonly routeNavigationService = inject(RouteNavigationService);
  private readonly alertHelper = inject(AlertHelper);

  public blueprint = model<ComplexArticleBlueprintDTO>();

  public versionOverwriteMode = model<ComplexArticleBlueprintVersionOverwriteMode>('only-new-articles');

  public isInternal = model<boolean>(false);

  public isNotSearchable = model<boolean>(false);

  public validationErrors = signal<ComplexArticleBlueprintValidation>({
    valid: true,
    errorsText: undefined,
  });

  constructor(
  ) {
    effect(() => {
      this.reValidate(this.blueprint());
    });
  }

  public close() {
    this.bsModalRef.hide();
  }

  public async execute(): Promise<void> {
    const blueprint = this.blueprint();
    if(!Log.assertExists(blueprint)) return;

    const validation = this.complexArticleBlueprintService.validateBlueprint(blueprint);

    if(!validation.valid) {
      this.alertHelper.showError(`complexArticleBlueprint.validation.error.in_blueprint`, validation.errorsText);
      return;
    }

    await this.routeNavigationService.toConfigurator();

    this.bsModalRef.setClass('modal-lg d-none');

    const generatedArticleNos = await this.complexArticleBlueprintService.runBlueprint(
      blueprint, {
        internal: this.isInternal(),
        notSearchable: this.isNotSearchable(),
        versionOverwriteMode: this.versionOverwriteMode()
      });

    this.alertHelper.showMessage('complexArticleBlueprint.title.generated_overview',this.createArticleNosLinksHtml(generatedArticleNos))

    this.bsModalRef.setClass('modal-lg');
    await this.routeNavigationService.toAdmintool();
  }

  private createArticleNosLinksHtml(articleNos: string[]): string {
    if(articleNos.length == 0) return '-';
    return articleNos.map(articleNo => `<a href="admintool#?function=loadarticle&articleNo=${articleNo}" target="_blank">${articleNo} ↗</a>`)
      .join('<br>');
  }

  protected reValidate(blueprint: ComplexArticleBlueprintDTO | undefined): void {
    if(!blueprint) return;
    this.validationErrors.set(this.complexArticleBlueprintService.validateBlueprint(blueprint));
  }
}
